import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from "@mui/material";
import { IoSettingsSharp } from "react-icons/io5";
import { postData, getData } from "../../Webservice/Webservice";

const ShippingPaymentConfig = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    B2C: {
      codAvailable: true,
      codCharge: 0,
      codThreshold: 0,
      deliveryCharge: 0,
      deliveryChargeRemovalThreshold: 0
    },
    B2B: {
      codAvailable: true,
      codCharge: 0,
      codThreshold: 0,
      deliveryCharge: 0,
      deliveryChargeRemovalThreshold: 0
    }
  });

  useEffect(() => {
    const fetchSettings = async () => {
      setInitialLoading(true);
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const response = await getData("delivery-setting/view", null, token);

        if (response?.setting) {
          const { B2B, B2C } = response.setting;
          setFormData({ B2B, B2C });
        } else {
          console.warn("No setting data found in response");
        }
      } catch (err) {
        console.error("Failed to load settings", err);
        setError("Failed to load settings. Using default values.");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setError("");
    setSuccess("");
  };

  const handleChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: field === "codAvailable" ? value : Number(value)
      }
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await postData("delivery-setting/update", formData, token);
      setSuccess("Delivery settings updated successfully!");
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.message || "Failed to update settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Feature Item Component
  const FeatureItem = ({ label, value, icon = "₹" }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
        {icon} {value}
      </Typography>
    </Box>
  );

  return (
    <>
      <Box sx={{ p: 3, backgroundColor: "#f5f7f9", minHeight: "100vh" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ color: "text.primary", fontFamily: "Mulish, sans-serif" }}>
            Shipping & Payment Configuration
          </Typography>
          <Button
            variant="contained"
            startIcon={<IoSettingsSharp />}
            onClick={handleOpen}
            sx={{
              backgroundColor: "rgb(255, 209, 2)",
              border: "1px solid black",
              color: "black",
              padding: "10px 20px",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              fontFamily: "Mulish, sans-serif",
              fontSize: "0.95rem",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgb(230, 189, 0)",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
              }
            }}
          >
            Update Settings
          </Button>
        </Box>

        {initialLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Cash on Delivery Card */}
            <Grid item xs={12} md={6}>
              <Card 
                elevation={0}
                sx={{ 
                  height: "100%",
                  borderRadius: 3,
                  border: "2px solid #e0e0e0",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "visible",
                  // "&:hover": { 
                  //   boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  //   transform: "translateY(-4px)",
                  //   borderColor: "#ffd102"
                  // }
                }}
              >
                <Box sx={{ 
                  height: "8px",
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px"
                }} />
                
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" , fontFamily: "Mulish, sans-serif"}}>
                      💵 Cash on Delivery
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={`B2C ${formData.B2C.codAvailable ? '✓' : '✗'}`}
                        sx={{ 
                          backgroundColor: formData.B2C.codAvailable ? "#4CAF50" : "#f44336",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.75rem"
                        }} 
                      />
                      <Chip 
                        label={`B2B ${formData.B2B.codAvailable ? '✓' : '✗'}`}
                        sx={{ 
                          backgroundColor: formData.B2B.codAvailable ? "#4CAF50" : "#f44336",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.75rem"
                        }} 
                      />
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                    Configure payment on delivery settings for both B2C and B2B customers
                  </Typography>

                  {/* B2C Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#764ba2", mb: 2 }}>
                      Business to Customer (B2C)
                    </Typography>
                    <Box sx={{ backgroundColor: "#f8f9fa", borderRadius: 2, p: 2 }}>
                      <FeatureItem label="COD Charges" value={formData.B2C.codCharge} />
                      <Divider sx={{ my: 1 }} />
                      <FeatureItem label="Maximum COD Amount" value={formData.B2C.codThreshold} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}>
                      {formData.B2C.codAvailable ? (
                        <>💡 Available for orders below ₹{formData.B2C.codThreshold}</>
                      ) : (
                        <>❌ Cash on Delivery is currently disabled</>
                      )}
                    </Typography>
                  </Box>

                  {/* B2B Section */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#764ba2", mb: 2 }}>
                      Business to Business (B2B)
                    </Typography>
                    <Box sx={{ backgroundColor: "#f8f9fa", borderRadius: 2, p: 2 }}>
                      <FeatureItem label="COD Charges" value={formData.B2B.codCharge} />
                      <Divider sx={{ my: 1 }} />
                      <FeatureItem label="Maximum COD Amount" value={formData.B2B.codThreshold} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}>
                      {formData.B2B.codAvailable ? (
                        <>💡 Available for orders below ₹{formData.B2B.codThreshold}</>
                      ) : (
                        <>❌ Cash on Delivery is currently disabled</>
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Free Shipping Card */}
            <Grid item xs={12} md={6}>
              <Card 
                elevation={0}
                sx={{ 
                  height: "100%",
                  borderRadius: 3,
                  border: "2px solid #e0e0e0",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "visible",
                  // "&:hover": { 
                  //   boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  //   transform: "translateY(-4px)",
                  //   borderColor: "#ffd102"
                  // }
                }}
              >
                <Box sx={{ 
                  height: "8px",
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px"
                }} />
                
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", fontFamily: "Mulish, sans-serif" }}>
                       🚚 Free Shipping
                      </Typography>
                    </Box>
                    <Chip 
                      label="DELIVERY" 
                      sx={{ 
                        backgroundColor: "#f5576c",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.75rem"
                      }} 
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                    Set delivery charges and free shipping thresholds for your customers
                  </Typography>

                  {/* B2C Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#764ba2", mb: 2 }}>
                      Business to Customer (B2C)
                    </Typography>
                    <Box sx={{ backgroundColor: "#f8f9fa", borderRadius: 2, p: 2 }}>
                      <FeatureItem label="Delivery Charges" value={formData.B2C.deliveryCharge} />
                      <Divider sx={{ my: 1 }} />
                      <FeatureItem label="Free Shipping Above" value={formData.B2C.deliveryChargeRemovalThreshold} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}>
                      🎉 Free delivery for orders above ₹{formData.B2C.deliveryChargeRemovalThreshold}
                    </Typography>
                  </Box>

                  {/* B2B Section */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#764ba2", mb: 2 }}>
                      Business to Business (B2B)
                    </Typography>
                    <Box sx={{ backgroundColor: "#f8f9fa", borderRadius: 2, p: 2 }}>
                      <FeatureItem label="Delivery Charges" value={formData.B2B.deliveryCharge} />
                      <Divider sx={{ my: 1 }} />
                      <FeatureItem label="Free Shipping Above" value={formData.B2B.deliveryChargeRemovalThreshold} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}>
                      🎉 Free delivery for orders above ₹{formData.B2B.deliveryChargeRemovalThreshold}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Update Modal */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle variant="h5" sx={{ fontWeight: 600, fontFamily: "Mulish, sans-serif" }}>
          Update Delivery & Payment Settings
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Grid container spacing={3}>
            {/* B2C Section */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 2, color: "grey", fontWeight: 600 }}>
                B2C (Business to Customer)
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.B2C.codAvailable}
                    onChange={(e) => handleChange("B2C", "codAvailable", e.target.checked)}
                    color="primary"
                  />
                }
                label="COD Available"
                sx={{ mb: 2 }}
              />

              <TextField
                label="COD Charge (₹)"
                type="number"
                fullWidth
                value={formData.B2C.codCharge}
                onChange={(e) => handleChange("B2C", "codCharge", e.target.value)}
                disabled={!formData.B2C.codAvailable}
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
              />

              <TextField
                label="Max COD Amount (₹)"
                type="number"
                fullWidth
                value={formData.B2C.codThreshold}
                onChange={(e) => handleChange("B2C", "codThreshold", e.target.value)}
                disabled={!formData.B2C.codAvailable}
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
              />

              <TextField
                label="Delivery Charge (₹)"
                type="number"
                fullWidth
                value={formData.B2C.deliveryCharge}
                onChange={(e) => handleChange("B2C", "deliveryCharge", e.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
              />

              <TextField
                label="Free Shipping Above (₹)"
                type="number"
                fullWidth
                value={formData.B2C.deliveryChargeRemovalThreshold}
                onChange={(e) => handleChange("B2C", "deliveryChargeRemovalThreshold", e.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* B2B Section */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 2, color: "grey", fontWeight: 600 }}>
                B2B (Business to Business)
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.B2B.codAvailable}
                    onChange={(e) => handleChange("B2B", "codAvailable", e.target.checked)}
                    color="primary"
                  />
                }
                label="COD Available"
                sx={{ mb: 2 }}
              />

              <TextField
                label="COD Charge (₹)"
                type="number"
                fullWidth
                value={formData.B2B.codCharge}
                onChange={(e) => handleChange("B2B", "codCharge", e.target.value)}
                disabled={!formData.B2B.codAvailable}
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
              />

              <TextField
                label="Max COD Amount (₹)"
                type="number"
                fullWidth
                value={formData.B2B.codThreshold}
                onChange={(e) => handleChange("B2B", "codThreshold", e.target.value)}
                disabled={!formData.B2B.codAvailable}
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
              />

              <TextField
                label="Delivery Charge (₹)"
                type="number"
                fullWidth
                value={formData.B2B.deliveryCharge}
                onChange={(e) => handleChange("B2B", "deliveryCharge", e.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
              />

              <TextField
                label="Free Shipping Above (₹)"
                type="number"
                fullWidth
                value={formData.B2B.deliveryChargeRemovalThreshold}
                onChange={(e) => handleChange("B2B", "deliveryChargeRemovalThreshold", e.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading} sx={{ color: "grey" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{
              marginRight: "12px",
              border: "1px solid black",
              backgroundColor: "rgb(255, 209, 2)",
              color: "black",
              "&:hover": { backgroundColor: "rgb(230, 189, 0)" }
            }}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShippingPaymentConfig;